import { useState } from 'react'
import { PromptCoach } from './components/PromptCoach'
import { EducationModule } from './components/EducationModule'
import { AccessibilityPanel } from './components/AccessibilityPanel'
import { SeniorButton } from './components/SeniorButton'
import { useKeyboardShortcuts } from './components/KeyboardNavigation'
import { AccessibilityProvider, useAccessibility } from './contexts/AccessibilityContext'

type ActiveTab = 'coach' | 'education'

function AppContent() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('coach')
  const { settings } = useAccessibility()
  
  // Enable keyboard shortcuts
  useKeyboardShortcuts()

  const getTitleSize = () => {
    switch (settings.fontSize) {
      case 'small': return 'text-3xl'
      case 'medium': return 'text-4xl'
      case 'large': return 'text-5xl'
      case 'extra-large': return 'text-6xl'
    }
  }

  const getSubtitleSize = () => {
    switch (settings.fontSize) {
      case 'small': return 'text-lg'
      case 'medium': return 'text-xl'
      case 'large': return 'text-2xl'
      case 'extra-large': return 'text-3xl'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Перейти к основному содержанию
      </a>

      <AccessibilityPanel />
      
      <div className="text-center mb-8">
        <h1 className={`${getTitleSize()} font-bold text-gray-900 mb-4`}>
          Говори с Машиной
        </h1>
        <p className={`${getSubtitleSize()} text-gray-600 mb-6`}>
          Простое изучение искусственного разума
        </p>

        {/* Navigation tabs */}
        <nav className="flex justify-center gap-4 mb-8" role="tablist">
          <SeniorButton
            onClick={() => setActiveTab('coach')}
            variant={activeTab === 'coach' ? 'primary' : 'secondary'}
            size="large"
            icon="📝"
            role="tab"
            aria-selected={activeTab === 'coach'}
            aria-controls="coach-panel"
          >
            Потренируемся
          </SeniorButton>
          <SeniorButton
            onClick={() => setActiveTab('education')}
            variant={activeTab === 'education' ? 'primary' : 'secondary'}
            size="large"
            icon="🎓"
            role="tab"
            aria-selected={activeTab === 'education'}
            aria-controls="education-panel"
          >
            Как это работает
          </SeniorButton>
        </nav>
      </div>

      {/* Content based on active tab */}
      <main 
        id="main-content" 
        className="focus:outline-none"
        tabIndex={-1}
      >
        <div
          id="coach-panel"
          role="tabpanel"
          aria-labelledby="coach-tab"
          hidden={activeTab !== 'coach'}
        >
          {activeTab === 'coach' && <PromptCoach />}
        </div>
        <div
          id="education-panel"
          role="tabpanel"
          aria-labelledby="education-tab"
          hidden={activeTab !== 'education'}
        >
          {activeTab === 'education' && <EducationModule />}
        </div>
      </main>
    </div>
  )
}

function App() {
  return (
    <AccessibilityProvider>
      <AppContent />
    </AccessibilityProvider>
  )
}

export default App